import get from "lodash/get";
import uniqBy from "lodash/uniqBy";
import keyBy from "lodash/keyBy";
import { MutableType } from "actions/mutate";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    ITEMS_ID,
    ITEM_DISPLAY_PREFERENCES_ID,
    ROOT_ID,
    ROOT_NAMESPACE,
} from "constants/product-collection";
import { ItemKind } from "containers/folders/types/base";
import { nameExtRemover } from "containers/folders/utils/space-item";
import {
    aggregateCollectionAllocationDimensions,
    enableAllSelectedDimensions,
} from "epics/collections/helpers/rollups";
import { getUserDefinedClassificationName, ROLLUP_ALLOCATION_DIMENSION_DELIMITER } from "./allocation-dimension";

const getDimensionId = (objectId: string) => (objectId || "").replace(ALLOCATION_DIMENSION_ID_PREFIX, "");

export const mapAllocationDimension = (allocation) =>
    allocation?.data?.reduce((acc, dimension) => {
        const dimensionId = getDimensionId(dimension.ref.object_id);
        acc[`${dimensionId}${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}${dimension.ref.document_id}`] = {
            ...dimension,
            ref: {
                ...dimension.ref,
                document_id: "",
                object_id: `${dimension.ref.object_id}${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}${dimension.ref.document_id}`,
            },
        };
        return acc;
    }, {});

const mergeAllocationDimensions = (allocations) => {
    const aggregateDimensionValues = (dimensionValues, collectionId, userDefinedGroups) => {
        return dimensionValues.map((dimensionValue) => {
            const initialFields = { ...dimensionValue.struct_value.fields };
            return {
                struct_value: {
                    ...dimensionValue.struct_value,
                    fields: !userDefinedGroups.length
                        ? initialFields
                        : userDefinedGroups.reduce((acc, cg) => {
                              if (acc[cg.field_name]) {
                                  acc[cg.field_name][collectionId] = true;

                                  // Rename each key of fields in order to support multiple key fields with same name
                                  acc[getUserDefinedClassificationName(cg.field_name, collectionId)] = {
                                      ...acc[cg.field_name],
                                  };
                                  // Remove original key-value of fields
                                  delete acc[cg.field_name];
                              }

                              return acc;
                          }, initialFields),
                },
            };
        });
    };

    const mergeExistingAllocationValues = (dimensionValuesAcc, currentAllocationDimensionValues) => {
        return dimensionValuesAcc.map((dimensionValue) => {
            const currentAllocationDimensionValue = currentAllocationDimensionValues.find(
                (it) => it.struct_value?.id === dimensionValue.struct_value?.id
            );
            if (!currentAllocationDimensionValue) {
                return dimensionValue;
            }

            return {
                struct_value: {
                    ...dimensionValue.struct_value,
                    fields: {
                        ...dimensionValue.struct_value.fields,
                        ...currentAllocationDimensionValue.struct_value.fields,
                    },
                },
            };
        });
    };

    return Object.keys(allocations).reduce((acc, key) => {
        const [dimensionId, collectionId] = key.split("==");
        const dimensionFound = !!acc[dimensionId];
        const isGeneric =
            get(allocations[key], [
                "mutable_mixed",
                "objects",
                "field_map",
                "map",
                "fields",
                "subtype",
                "string_value",
            ]) === "GENERIC";
        const dimensionKeys = get(
            allocations[key],
            ["mutable_mixed", "objects", "field_map", "map", "fields", "attributes", "struct_value", "keys"],
            []
        );
        const dimensionKeysAcc = dimensionFound
            ? get(
                  acc[dimensionId],
                  ["mutable_mixed", "objects", "field_map", "map", "fields", "attributes", "struct_value", "keys"],
                  []
              )
            : [];
        const dimensionValues = get(
            allocations[key],
            ["mutable_mixed", "objects", "dimension_values", "list", "values"],
            []
        );
        const dimensionValuesAcc = dimensionFound
            ? get(acc[dimensionId], ["mutable_mixed", "objects", "dimension_values", "list", "values"], [])
            : [];

        const userDefinedGroups = dimensionKeys.reduce((iacc, it) => {
            if (!it.display?.is_user_defined) {
                return iacc;
            }

            iacc.push({
                ...it,
                ...(collectionId && { collectionId }),
            });
            return iacc;
        }, []);

        const currentAllocationDimensionValues = aggregateDimensionValues(
            dimensionValues,
            collectionId,
            userDefinedGroups
        );
        acc[dimensionId] = {
            mutable_mixed: {
                objects: {
                    field_map: {
                        map: {
                            fields: {
                                ...get(
                                    allocations[key],
                                    ["mutable_mixed", "objects", "field_map", "map", "fields"],
                                    {}
                                ),
                                attributes: {
                                    struct_value: {
                                        ...get(
                                            allocations[key],
                                            [
                                                "mutable_mixed",
                                                "objects",
                                                "field_map",
                                                "map",
                                                "fields",
                                                "attributes",
                                                "struct_value",
                                            ],
                                            {}
                                        ),
                                        keys: dimensionFound
                                            ? [...dimensionKeysAcc, ...userDefinedGroups]
                                            : [
                                                  ...dimensionKeys.filter((it) => !it.display?.is_user_defined),
                                                  ...userDefinedGroups,
                                              ],
                                    },
                                },
                            },
                        },
                    },
                    ...(isGeneric && {
                        dimension_values: {
                            list: {
                                values: dimensionFound
                                    ? uniqBy(
                                          [
                                              ...mergeExistingAllocationValues(
                                                  dimensionValuesAcc,
                                                  currentAllocationDimensionValues
                                              ),
                                              ...currentAllocationDimensionValues,
                                          ],
                                          "struct_value.id"
                                      )
                                    : currentAllocationDimensionValues,
                            },
                        },
                    }),
                },
            },
            ref: {
                ...allocations[key].ref,
                object_id: allocations[key].ref.object_id.replace(`==${collectionId}`, ""),
            },
        };
        return acc;
    }, {});
};

export const getAndAggregateAllocationDimensions = (rollupSnapshot) => {
    const allocationsDimensions = [];
    rollupSnapshot.allocation_dimensions.forEach((allocationsDimension) => {
        allocationsDimensions.push(...allocationsDimension.data);
    });
    const baseCollection = {
        snapshot: {
            allocation_dimensions: mapAllocationDimension({
                data: allocationsDimensions,
            }),
        },
    };

    const allCollections = rollupSnapshot.allocation_dimensions.map((allocation) => ({
        snapshot: {
            allocation_dimensions: mapAllocationDimension(allocation),
        },
    }));

    enableAllSelectedDimensions(baseCollection, allCollections);
    aggregateCollectionAllocationDimensions(baseCollection, allCollections);

    return mergeAllocationDimensions(baseCollection.snapshot.allocation_dimensions);
};

export const overrideDoorClassifications = (allocationDimensions, merchantDoorClassifications) => {
    if (!merchantDoorClassifications) {
        return allocationDimensions;
    }

    const { dimensionId, dimensionValues } = merchantDoorClassifications;
    const doorsMap = keyBy(dimensionValues, "id");
    const updatedDimensionValues = allocationDimensions[
        dimensionId
    ].mutable_mixed.objects.dimension_values.list.values.map((value) => {
        const { id, fields } = value.struct_value;
        return {
            struct_value: {
                ...value.struct_value,
                fields: {
                    ...fields,
                    ...doorsMap[id]?.fields,
                },
            },
        };
    });
    allocationDimensions[dimensionId].mutable_mixed.objects.dimension_values.list.values = updatedDimensionValues;
    return allocationDimensions;
};

export const getOrderForAllocationDimensionsValues = (rollupSnapshot) => {
    // Get allocationDimensions of the first assortment
    const items = rollupSnapshot.allocation_dimensions.length ? rollupSnapshot.allocation_dimensions[0].data : [];
    return items
        .map((allocationDimension) =>
            allocationDimension.mutable_mixed.objects?.dimension_values?.list?.values?.map(
                (value) => value.struct_value.id
            )
        )
        .filter((item) => !!item);
};

export const getAllocationDimensionRefs = (allocationDimensions) => {
    const dimensionKeys = Object.keys(allocationDimensions);

    return dimensionKeys.reduce((acc, key) => {
        const dimension = allocationDimensions[key];
        const dimensionId = getDimensionId(dimension.ref?.object_id);
        dimensionId && (acc[dimensionId] = dimension.ref);
        return acc;
    }, {});
};

export const getDimensionIds = (allocationDimensions) => {
    const dimensionKeys = Object.keys(allocationDimensions);

    return dimensionKeys.reduce((acc, key) => {
        const dimensionId = getDimensionId(allocationDimensions[key].ref?.object_id);
        dimensionId && acc.push(dimensionId);
        return acc;
    }, []);
};

export const getCollectionRefs = (rollupSnapshot) => {
    return rollupSnapshot.collection_refs.reduce((acc, it) => {
        acc[it.collection_id] = nameExtRemover(it.collection_name, ItemKind.CollectionDocument);
        return acc;
    }, {});
};

export const getSavedViews = (fields, collectionRefs) => {
    const savedViews = [];
    const collectionIds = Object.entries(fields) as any;

    collectionIds.forEach(([collectionId, itemDisplayPreferences]) => {
        itemDisplayPreferences?.list_value.forEach((item) => {
            savedViews.push({
                struct_value: {
                    ...item,
                    collectionId,
                    collectionName: collectionRefs[collectionId],
                },
            });
        });
    });

    savedViews.sort((a, b) => {
        const aName = a.struct_value.collectionName;
        const bName = b.struct_value.collectionName;
        return aName.localeCompare(bName);
    });

    return savedViews;
};

export const getOptimizedSavedView = (rollupSnapshot, collectionRefs) => {
    const savedViews = [];

    rollupSnapshot.item_display_preference_refs.forEach((field) => {
        const collectionId = field.ref.document_id;
        field.names.forEach((name, index) => {
            savedViews.push({
                struct_value: {
                    ref: {
                        ...field.ref,
                        collection_id: field.ref.document_id,
                        merchant_id: rollupSnapshot.merchant_id,
                        rollup_snapshot_id: rollupSnapshot.id,
                    },
                    id: field.ids[index],
                    name,
                    collectionId,
                    collectionName: collectionRefs[collectionId],
                },
            });
        });
    });

    savedViews.sort((a, b) => {
        const aName = a.struct_value.collectionName;
        const bName = b.struct_value.collectionName;
        return aName.localeCompare(bName);
    });

    return savedViews;
};

export const getAllocationDimensionsOrder = (rollupSnapshot) => {
    return rollupSnapshot.collection_refs.map((refs) => {
        return {
            collectionId: refs.collection_id,
            allocationDimensions: refs.allocation_dimensions?.map((it) => it.object_id) || [],
        };
    });
};

export const buildServerRollupSchema = (rollupSnapshot, merchantDoorClassifications = null) => {
    const orderForAllocationDimensionsValues = getOrderForAllocationDimensionsValues({ ...rollupSnapshot });
    const allocationDimensions = overrideDoorClassifications(
        getAndAggregateAllocationDimensions({ ...rollupSnapshot }),
        merchantDoorClassifications
    );
    const collectionRefs = getCollectionRefs(rollupSnapshot);
    const allocationDimensionRefs = getAllocationDimensionRefs(allocationDimensions);
    const dimensionIds = getDimensionIds(allocationDimensions);
    const assortmentSavedViews = rollupSnapshot.item_display_preferences
        ? getSavedViews(rollupSnapshot.item_display_preferences.mutable_map.fields, collectionRefs)
        : getOptimizedSavedView(rollupSnapshot, collectionRefs);
    const reviewTabSavedViews = getSavedViews(
        rollupSnapshot.review_display_preference.mutable_map.fields,
        collectionRefs
    );
    // Get itemDisplayPreferencesObjectId from the first assortment
    const itemDisplayPreferencesObjectId = rollupSnapshot.collection_refs[0].item_display_preferences.object_id;
    // Get reviewDisplayPreferences from the first assortment
    const reviewDisplayPreferences = rollupSnapshot.collection_refs[0].review_display_preferences;

    return {
        id: rollupSnapshot.id,
        item_id: rollupSnapshot.id,
        merchant_id: rollupSnapshot.merchant_id, // this should be old id
        merchant_v1_id: rollupSnapshot.merchant_id,
        name: rollupSnapshot.name,
        items_ref: {
            namespace: ROOT_NAMESPACE,
            document_id: "",
            object_id: ITEMS_ID,
            branch: "",
            revision: "0",
            type: MutableType.List,
        },
        allocation_dimension_refs: allocationDimensionRefs,
        item_allocation_dimension_order: dimensionIds,
        snapshot: {
            allocation_dimensions: allocationDimensions,
            item_display_preferences: {
                mutable_list: {
                    values: assortmentSavedViews,
                },
                ref: {
                    namespace: ROOT_NAMESPACE,
                    document_id: "",
                    object_id: ITEM_DISPLAY_PREFERENCES_ID,
                    branch: "",
                    revision: "0",
                    type: MutableType.List,
                },
            },
            review_display_preferences: {
                ref: reviewDisplayPreferences,
                mutable_list: {
                    values: reviewTabSavedViews,
                },
            },
            items: {
                mutable_list: { values: [] },
                ref: {
                    namespace: ROOT_NAMESPACE,
                    document_id: "",
                    object_id: ITEMS_ID,
                    branch: "",
                    revision: "0",
                    type: MutableType.List,
                },
            },
            root: {
                ref: {
                    namespace: ROOT_NAMESPACE,
                    document_id: "",
                    object_id: ROOT_ID,
                    branch: "",
                    revision: "0",
                    type: MutableType.Map,
                },
                mutable_map: {
                    fields: {
                        allocation_dimension_refs: {
                            struct_value: allocationDimensionRefs,
                        },
                        collections_refs: collectionRefs,
                        folder_item_id: { string_value: rollupSnapshot.id },
                        item_allocation_dimension_order: {
                            list_value: dimensionIds,
                        },
                        item_display_preference_object_id: {
                            ref: {
                                object_id: itemDisplayPreferencesObjectId,
                            },
                        },
                        item_display_property: rollupSnapshot.item_display_property,
                        name: { string_value: "" },
                        merchant_id: { string_value: rollupSnapshot.merchant_id },
                        merchant_v1_id: { string_value: rollupSnapshot.merchant_id },
                        review_display_preference_object_id: {
                            ref: reviewDisplayPreferences,
                        },
                        orderForAllocationDimensionsValues,
                    },
                },
            },
        },
    };
};
